import { lazy, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchCurrentUser } from '../../../shared/api';
import { ModuleLayout } from '../../../widgets/module-layout';
const AssignmentSchedulePage = lazy(() => import('./AssignmentSchedulePage').then((m) => ({ default: m.AssignmentSchedulePage })));
const BranchManagersPage = lazy(() => import('./BranchManagersPage').then((m) => ({ default: m.BranchManagersPage })));
const BranchesPage = lazy(() => import('./BranchesPage').then((m) => ({ default: m.BranchesPage })));
const CheckinPage = lazy(() => import('./CheckinPage').then((m) => ({ default: m.CheckinPage })));
const CrossReportPage = lazy(() => import('./CrossReportPage').then((m) => ({ default: m.CrossReportPage })));
const DashboardPage = lazy(() => import('./DashboardPage').then((m) => ({ default: m.DashboardPage })));
const EmptyReportPage = lazy(() => import('./EmptyReportPage').then((m) => ({ default: m.EmptyReportPage })));
const LarkMappingPage = lazy(() => import('./LarkMappingPage').then((m) => ({ default: m.LarkMappingPage })));
const ReportPage = lazy(() => import('./ReportPage').then((m) => ({ default: m.ReportPage })));
const SchedulePage = lazy(() => import('./SchedulePage').then((m) => ({ default: m.SchedulePage })));
const ScorePage = lazy(() => import('./ScorePage').then((m) => ({ default: m.ScorePage })));
const WorkerPage = lazy(() => import('./WorkerPage').then((m) => ({ default: m.WorkerPage })));
const ZoneQrPage = lazy(() => import('./ZoneQrPage').then((m) => ({ default: m.ZoneQrPage })));

/**
 * Dinh tuyen Checklist (/cleaning/...) — GIU NGUYEN duong dan Django de link cu,
 * bookmark va MA QR DA IN (/cleaning/check/<uuid>/) van mo dung trang.
 *
 * Trang nhan vien, trang quet QR va anh QR khong co sidebar (dung tren dien thoai /
 * de in), giong ban Django.
 */
export const CLEANING_STANDALONE_RE = /^\/cleaning\/(worker|check)(\/|$)|^\/cleaning\/zones\/\d+\/qr\/?$/;

export function CleaningStandalone() {
  return (
    <Routes>
      <Route path="/cleaning/worker" element={<WorkerPage />} />
      <Route path="/cleaning/check/:token" element={<CheckinPage />} />
      {/* Cac URL con cu cua trang QR (submit/complete/checkout) la POST — neu ai mo bang GET thi ve trang quet. */}
      <Route path="/cleaning/check/:token/*" element={<CheckinRedirect />} />
      <Route path="/cleaning/zones/:pk/qr" element={<ZoneQrPage />} />
    </Routes>
  );
}

function CheckinRedirect() {
  const token = window.location.pathname.split('/')[3] ?? '';
  return <Navigate to={`/cleaning/check/${token}/`} replace />;
}

/**
 * /cleaning/: Django chi cho nguoi co cleaning.manager.view (con lai bi tu choi);
 * cong ung dung cu dua nhan vien thang vao /cleaning/worker/. Ban TS gop hai y do:
 * ai co quyen bao cao thi thay Tong quan, con lai chuyen sang trang nhan vien.
 */
function CleaningHome() {
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: fetchCurrentUser });
  if (!me.data) return <div className="cl">Đang tải...</div>;
  const u = me.data;
  const can = (p: string) => u.isStaff || u.isSuperuser || u.permissions.includes(p);
  if (can('cleaning.manager.view')) return <DashboardPage />;
  if (can('cleaning.admin')) return <Navigate to="/cleaning/branches/" replace />;
  return <Navigate to="/cleaning/worker/" replace />;
}

// checkItem=false: trang chu tu dua nguoi khong co quyen bao cao sang trang dung (khong /403).
const wrap = (href: string, el: ReactElement, checkItem = true) => (
  <ModuleLayout moduleKey="cleaning" activeHref={href} checkItem={checkItem}>
    {el}
  </ModuleLayout>
);

/** Cac <Route> co sidebar — chen vao <Routes> chinh cua App. */
export function cleaningRoutes() {
  return [
    <Route key="cl-home" path="/cleaning" element={wrap('/cleaning/', <CleaningHome />, false)} />,
    <Route key="cl-report" path="/cleaning/report" element={wrap('/cleaning/report/', <ReportPage />)} />,
    <Route key="cl-score" path="/cleaning/score" element={wrap('/cleaning/score/', <ScorePage />)} />,
    <Route key="cl-cross" path="/cleaning/cross-report" element={wrap('/cleaning/cross-report/', <CrossReportPage />)} />,
    <Route key="cl-empty" path="/cleaning/schedule-empty-report" element={wrap('/cleaning/schedule-empty-report/', <EmptyReportPage />)} />,
    <Route key="cl-branches" path="/cleaning/branches" element={wrap('/cleaning/branches/', <BranchesPage />)} />,
    <Route key="cl-zones" path="/cleaning/zones" element={<Navigate to="/cleaning/branches/" replace />} />,
    <Route key="cl-locations" path="/cleaning/locations" element={<Navigate to="/cleaning/branches/" replace />} />,
    <Route key="cl-mgr" path="/cleaning/branch-managers" element={wrap('/cleaning/branch-managers/', <BranchManagersPage />)} />,
    <Route key="cl-sched" path="/cleaning/schedule" element={wrap('/cleaning/schedule/', <SchedulePage />)} />,
    <Route key="cl-asgn" path="/cleaning/assignments/schedule" element={wrap('/cleaning/schedule/', <AssignmentSchedulePage />)} />,
    <Route key="cl-lark" path="/cleaning/lark-mapping" element={wrap('/cleaning/lark-mapping/', <LarkMappingPage />)} />,
    <Route key="cl-any" path="/cleaning/*" element={<Navigate to="/cleaning/" replace />} />,
  ];
}
